/**
 * PSP/CRE Tool v2.0 - National Standards Constants
 *
 * Purpose: Define force structure standards for Romanian, NATO, US, Multinational,
 * German (Bundeswehr Heer), British (British Army), Polish (Wojska Lądowe), and
 * French (Armée de Terre) armies.
 * Each standard includes organizational structure, personnel strength, HQ casualty
 * modifiers, rear area security modifiers, support unit configurations, and
 * available brigade types.
 *
 * Author: Dr. Tatar Radu MD
 * Date: March 15, 2026
 * Version: 3.2
 *
 * v3.2 CHANGES (March 26, 2026) — BUG-183: AIRBORNE/AIR_ASSAULT/MOUNTAIN display ordering.
 *   Standardised order to AIRBORNE → AIR_ASSAULT → MOUNTAIN across all national standards,
 *   matching BRIGADE_MANEUVER_TYPES (Tab1) and BATTALION_TYPES (unitTypes.js).
 *   Fixed: ROMANIAN (MOUNTAIN↔AIRBORNE), NATO (MOUNTAIN→after AIR_ASSAULT),
 *   GERMAN (MOUNTAIN↔AIRBORNE), MULTINATIONAL (MOUNTAIN→after AIR_ASSAULT).
 *   Already correct: US, POLISH, FRENCH, BRITISH. No functional/calculation impact.
 *
 * Version: 3.1
 *
 * v3.1 CHANGES (March 15, 2026) — support_ratio added to all brigade blocks:
 *   Tab1 reads standard.brigade?.support_ratio to calculate support personnel count
 *   (supportCount = Math.round(strength × support_ratio)) at 4 call sites. Field was
 *   absent from all 8 national standards — 0.20 fallback firing universally.
 *   Values derived from each nation's support/CSS battalion as fraction of total brigade:
 *   ROMANIAN 0.20 (4-bn brigade, heavier organic support — coincides with fallback).
 *   NATO/US/MULTINATIONAL/BRITISH/FRENCH 0.16 (standard 3-bn BCT + support battalion).
 *   GERMAN 0.15 (Versorgungsbataillon — slightly leaner CSS footprint).
 *   POLISH 0.18 (4-bn brigade, heavier than NATO 3-bn but lighter than Romanian).
 *
 * Version: 3.0
 *
 * v3.0 CHANGES (March 15, 2026) — NS-17: Added FRENCH (Armée de Terre):
 *   NATIONAL_STANDARDS.FRENCH: Scorpion-era brigade interarmes (BIA) structure.
 *     Brigade strength 4500 (Scorpion BIA doctrinal target). Battalion 950 (régiment
 *     analog). Division 25000 (Scorpion division model, 3 BIA + 3 endivisioned rgts).
 *     Corps 65000. Available brigade types: HEAVY (Leclerc), MECHANIZED (VBCI/Griffon),
 *     INFANTRY, ARTILLERY, AIRBORNE (11e BP), MOUNTAIN (27e BIM), LOGISTICS, MEDICAL.
 *     rear_area_security_modifier: 0.92 — ALAT provides air-mobile security.
 *     Source: LPM 2024-2030, Scorpion programme, Armée de Terre transformation 2024.
 *   MTF_POSITIONS.FRENCH: Role 1 = 10km, Role 2 = 24km, Role 3 = 110km.
 *     No French-specific deviation from NATO found; Role 2 marginally closer per GMC
 *     (Groupement Médico-Chirurgical) forward-positioning doctrine.
 *   SUPPORT_UNITS.FRENCH: Régiment Médical (RMED, corps) + Brigade Logistique (B.LOG).
 *     B.LOG created March 2024; commands 8 Train regiments; sustains 2 French + 1 allied
 *     brigades at division level. Source: defense.gouv.fr, March 2024.
 *   SUPPORT_UNITS_BY_ECHELON.FRENCH: French doctrinal unit names throughout.
 *     Battalion: section_transmissions, groupe_medical_campagne, section_ravitaillement.
 *     Brigade: CCT, compagnie_medicale, bataillon_soutien_logistique, compagnie_genie,
 *       groupe_artillerie (Caesar SPH), escadron_eclairage.
 *     Division: bataillon_commandement_transmissions, groupement_medico_chirurgical
 *       (Role 2E), regiment_soutien_logistique, bataillon_genie, regiment_artillerie,
 *       batterie_defense_sol_air.
 *     Corps: brigade_transmissions, regiment_medical (RMED, Role 3), brigade_logistique
 *       (B.LOG), brigade_genie, 4e_brigade_aerocombat (ALAT — NH90 Caïman MEDEVAC,
 *       Tigre HAD attack, Caracal SOF).
 *   Doctrinal sources: LPM 2024-2030, Scorpion programme docs, defense.gouv.fr BIA
 *     structure, opex360.com, euro-sd.com SCORPION analysis, ALAT NH90 delivery 2025.
 *
 * v2.9 CHANGES (March 12, 2026) — BUG-074: Added STRYKER to USA and MULTINATIONAL
 *   available_brigade_types. STRYKER (Stryker BCT) existed in unitTypes.js since v2.5.0
 *   but had never been added here — stale comment claimed the key did not exist.
 *
 * v2.8 CHANGES (March 11, 2026) — Support Troops Gap Fix (NS-16):
 * - NS-16 (Issue 2 fix): Added `aviation_command` to SUPPORT_UNITS_BY_ECHELON.MULTINATIONAL.corps
 *   Strength 2200 = average of RO(1000)/NATO(2500)/US(4000)/DEU(2200)/GBR(2100)/POL(1800).
 *   In a MNC, aviation is contributed by framework/lead nation. ENH-018 (Lead Nation concept)
 *   will replace this with framework-nation-specific value when implemented.
 * - NS-16 (Issue 1 fix): Removed silent `|| SUPPORT_UNITS_BY_ECHELON.NATO` fallback in
 *   getSupportUnitsForEchelon(). All 7 standards are defined; fallback was dead code
 *   violating zero-fallback policy (APP_RULES 6.1). Now throws explicit error if an
 *   unknown standard is passed, directing developer to add SUPPORT_UNITS_BY_ECHELON entry.
 *
 * v2.7 CHANGES (February 22, 2026) — Phase 0-A Architecture Consolidation:
 * - NS-01: Added `hq_casualty_modifier` into every echelon block (all 4 existing nations)
 *   Values moved from deleted HQ_CASUALTY_MODIFIERS standalone constant
 * - NS-02: Added `rear_area_security_modifier` + `rear_area_security_description` as
 *   top-level fields to all 4 existing nations (values from battleCasualtyModule local block)
 * - NS-03: DELETED `HQ_PERSONNEL` standalone constant — orphaned, wrong values
 *   (ROMANIAN corps was 650, authoritative value is 1500). All callers use echelon blocks.
 * - NS-04: DELETED `HQ_CASUALTY_MODIFIERS` standalone constant — values moved to echelon blocks
 * - NS-05: DELETED `HQ_POSITIONS` standalone constant — values conflict with hq_position_feba_km
 *   (ROMANIAN corps: HQ_POSITIONS=100 vs hq_position_feba_km=50). Echelon-block values authoritative.
 * - NS-06: DELETED `ECHELON_BREAKDOWN` standalone constant — all fields computable from
 *   NATIONAL_STANDARDS directly. getEchelonBreakdown() now computes on the fly.
 * - NS-07: Updated `getHQPersonnel()` to read NATIONAL_STANDARDS[nation][echelon].hq_personnel
 *   Throws on missing data (APP_RULES 6.1 — no silent fallbacks)
 * - NS-08: Updated `getHQCasualtyModifier()` to read NATIONAL_STANDARDS[nation][echelon].hq_casualty_modifier
 *   Throws on missing data
 * - NS-09: Updated `getHQPosition()` to read NATIONAL_STANDARDS[nation][echelon].hq_position_feba_km
 *   Throws on missing data
 * - NS-10: Updated `getEchelonBreakdown()` to compute from NATIONAL_STANDARDS directly
 *   Returns: total_strength, hq_strength, combat_strength, support_strength, hq_percent, combat_percent
 * - NS-11: Updated `calculateTotalHQPersonnel()` to call getHQPersonnel() (not deleted HQ_PERSONNEL)
 * - NS-12: Updated `calculateHQCasualties()` to call getHQPersonnel() and getHQCasualtyModifier()
 * - NS-13: Added GERMAN (Bundeswehr Heer) to NATIONAL_STANDARDS, MTF_POSITIONS, SUPPORT_UNITS,
 *   SUPPORT_UNITS_BY_ECHELON — includes Heimatschutz integration, Bataillon/Brigade/Division/Korps structure
 * - NS-14: Added BRITISH (British Army) to all four structures — battlegroup-centric doctrine,
 *   RAMC/RLC/Royal Signals doctrinal unit names
 * - NS-15: Added POLISH (Wojska Lądowe) to all four structures — WOT territorial defence integration,
 *   Polish-language doctrinal unit names throughout
 * - NS-16: Updated exports — removed HQ_PERSONNEL, HQ_CASUALTY_MODIFIERS, HQ_POSITIONS,
 *   ECHELON_BREAKDOWN from named exports and default export object
 *
 * v2.6 CHANGES (February 11, 2026):
 * - CRITICAL ENHANCEMENT: Added 'breakdown_ratios' to battalion, brigade, division, corps
 * - Fixes BUG-065: Calculator now uses actual force structure instead of flat 4%/37% percentages
 *
 * v2.5 CHANGES (February 11, 2026):
 * - BUG FIX: Added 'AIRBORNE' to Romanian available_brigade_types
 *
 * v2.4 CHANGES (February 3, 2026):
 * - CRITICAL ENHANCEMENT: Added 'hq_position_feba_km' to all echelon blocks
 *
 * v2.3 CHANGES (February 1, 2026):
 * - CRITICAL FIX: Added 'hq_personnel' field to all echelon blocks
 *
 * References:
 * - Romanian Army TOE documentation
 * - NATO AJP-4.10, AJMedP-1 force structure standards
 * - US Army MTOE (Modified Table of Organization and Equipment)
 * - Bundeswehr Heer Heeresstruktur 5 (post-2016 reform)
 * - British Army Field Manual, Land Operations (AC 71940)
 * - Wojsko Polskie — Regulamin Działań Wojsk Lądowych
 * - Ukraine 2022-2024 force structure adaptations
 *
 * Usage:
 * import {
 *   NATIONAL_STANDARDS,
 *   MTF_POSITIONS,
 *   SUPPORT_UNITS,
 *   SUPPORT_UNITS_BY_ECHELON,
 *   TERRAIN_DEFENSE_MODIFIERS,
 *   getStandardConfig,
 *   getHQPersonnel,
 *   getHQCasualtyModifier,
 *   getHQPosition,
 *   getEchelonBreakdown,
 *   calculateHQCasualties
 * } from '../constants/nationalStandards';
 */

import { STATUS_ICONS, MILITARY_ICONS, FLAG_ICONS } from '../utils/icons';

// ============================================================================
// DEAD-002: Transport planning standards — used by transportCalculator.js
// ============================================================================

export const DEFAULT_TRANSPORT_STANDARDS = {
  min_dos_medical:       7,   // minimum Class VIII DOS to maintain forward (NATO STANAG 2143)
  convoy_interval_days:  3,   // standard push-resupply cycle (days)
  max_convoy_trucks:    10,   // max trucks per convoy (security / C2 limit)
  escort_required_above: 5,   // truck count above which armed escort is required
};

// ============================================================================
// NATIONAL STANDARDS - BASE STRUCTURE
// ============================================================================

export const NATIONAL_STANDARDS = {
  ROMANIAN: {
    id: 'ROMANIAN',
    flag: FLAG_ICONS.ROMANIA,
    label: 'Romanian Army Standard',
    shortLabel: 'ROU',
    icon: FLAG_ICONS.ROMANIA,

    // NS-02: Rear area security modifier (from battleCasualtyModule NATIONAL_REAR_AREA_MODIFIERS)
    rear_area_security_modifier: 0.95,
    rear_area_security_description: 'Romanian rear area protection with territorial defense integration',

    // v2.2 CSS-004: Available brigade types for this national standard
    available_brigade_types: [
      'HEAVY',
      'INFANTRY',
      'MECHANIZED',
      'AIRBORNE',       // 1st Paratroopers Brigade
      'MOUNTAIN',       // Vânători de Munte
      'ARTILLERY',
      'LOGISTICS',
      'ENGINEERS',      // 2026-10-24: engineer brigade (3 engineer battalions, support, HQ)
      'MEDICAL'
    ],
    css_default: 'LOGISTICS',

    // 2026-10-25 (Dr. Tatar, R1-05): the size fields (strength, hq_personnel, support_ratio, battalions, combat_ratio) of the Romanian blocks equal the NATO generic
    // values because no Romanian establishment table is public. Before: battalion 600, brigade 4,000 (4 battalions), division 12,000, corps 36,000. Positions and modifiers stay Romanian.
    battalion: {
      strength: 800,
      hq_personnel: 100,
      breakdown_ratios: {
        command: 0.50,
        support: 0.50
      },
      hq_position_feba_km: 8,
      hq_casualty_modifier: 0.30,    // NS-01: moved from HQ_CASUALTY_MODIFIERS.ROMANIAN.battalion
      defaultName: 'Battalion',      // BUG-NAME-001: English UI — was 'Batalion' (Romanian)
      combat_ratio: 0.70
    },
    brigade: {
      strength: 5000,
      hq_personnel: 200,
      breakdown_ratios: {
        command: 1.00,
        support: 0.00
      },
      hq_position_feba_km: 15,
      hq_casualty_modifier: 0.20,    // NS-01: moved from HQ_CASUALTY_MODIFIERS.ROMANIAN.brigade
      support_ratio: 0.16,           // NATO generic (decision of Dr. Tatar 2026-10-25: no public Romanian establishment table)
      battalions: 3,
      defaultName: 'Brigade',        // BUG-NAME-001: English UI — was 'Brigadă' (Romanian, non-ASCII)
      combat_ratio: 0.55
    },
    division: {
      strength: 20000,
      hq_personnel: 1000,
      breakdown_ratios: {
        command: 0.15,
        support: 0.85
      },
      hq_position_feba_km: 50,   // 2026-10-25 (Dr. Tatar): division headquarters further back; EDUCATED GUESS, expert judgment (no public doctrinal distance)
      hq_casualty_modifier: 0.15,    // NS-01: moved from HQ_CASUALTY_MODIFIERS.ROMANIAN.division
      brigades: 3,
      defaultName: 'Division',       // BUG-NAME-001: English UI — was 'Divizia' (Romanian)
      combat_ratio: 0.45
    },
    corps: {
      strength: 60000,
      hq_personnel: 2000,
      breakdown_ratios: {
        command: 0.10,
        support: 0.90
      },
      hq_position_feba_km: 90,   // 2026-10-25 (Dr. Tatar): corps headquarters further back; EDUCATED GUESS, expert judgment (no public doctrinal distance)
      hq_casualty_modifier: 0.10,    // NS-01: moved from HQ_CASUALTY_MODIFIERS.ROMANIAN.corps
      divisions: 3,
      defaultName: 'Corps',          // BUG-NAME-001: English UI — was 'Corp de Armată' (Romanian, non-ASCII)
      combat_ratio: 0.35
    },

    // DEAD-002: nation-specific transport planning factors
    transport_standards: {
      min_dos_medical:       5,   // NATO integration standard (RO 5 DOS forward)
      convoy_interval_days:  3,
      max_convoy_trucks:     8,
      escort_required_above: 4,
    },

    // DEAD-006: national medical supply DOS thresholds for generateSupplyWarnings()
    medical_logistics: {
      minimum_mdos_days:      5,   // required forward Class VIII stock
      warning_dos_threshold:  7,   // WARNING fires below this (vs hardcoded NATO 10)
    },

    notes: 'Romanian mechanized brigade — lean structure with 4 battalions. Forward HQ positioning reflects aggressive doctrine and smaller force structure.'
  },

  NATO: {
    id: 'NATO',
    flag: FLAG_ICONS.NATO,
    label: 'NATO Standard',
    shortLabel: 'NATO',
    icon: FLAG_ICONS.NATO,

    // NS-02: Rear area security modifier
    rear_area_security_modifier: 1.00,
    rear_area_security_description: 'NATO standard rear area protection (baseline)',

    // v2.2 CSS-004: Available brigade types
    available_brigade_types: [
      'HEAVY',
      'INFANTRY',
      'MECHANIZED',
      'AIRBORNE',
      'AIR_ASSAULT',
      'MOUNTAIN',
      'ARTILLERY',
      'AVIATION',
      'LOGISTICS',
      'ENGINEERS',      // 2026-10-24: engineer brigade (3 engineer battalions, support, HQ)
      'MEDICAL'
    ],
    css_default: 'LOGISTICS',

    battalion: {
      strength: 800,
      hq_personnel: 100,
      breakdown_ratios: {
        command: 0.50,
        support: 0.50
      },
      hq_position_feba_km: 10,
      hq_casualty_modifier: 0.30,    // NS-01: moved from HQ_CASUALTY_MODIFIERS.NATO.battalion
      defaultName: 'Battalion',
      combat_ratio: 0.70
    },
    brigade: {
      strength: 5000,
      hq_personnel: 200,
      breakdown_ratios: {
        command: 1.00,
        support: 0.00
      },
      hq_position_feba_km: 18,
      hq_casualty_modifier: 0.20,    // NS-01: moved from HQ_CASUALTY_MODIFIERS.NATO.brigade
      support_ratio: 0.16,           // Standard NATO 3-bn BCT + support bn (~800/5000)
      battalions: 3,
      defaultName: 'Brigade',
      combat_ratio: 0.55
    },
    division: {
      strength: 20000,
      hq_personnel: 1000,
      breakdown_ratios: {
        command: 0.15,
        support: 0.85
      },
      hq_position_feba_km: 50,   // 2026-10-25 (Dr. Tatar): division headquarters further back; EDUCATED GUESS, expert judgment (no public doctrinal distance)
      hq_casualty_modifier: 0.15,    // NS-01: moved from HQ_CASUALTY_MODIFIERS.NATO.division
      brigades: 3,
      defaultName: 'Division',
      combat_ratio: 0.45
    },
    corps: {
      strength: 60000,
      hq_personnel: 2000,
      breakdown_ratios: {
        command: 0.10,
        support: 0.90
      },
      hq_position_feba_km: 90,   // 2026-10-25 (Dr. Tatar): corps headquarters further back; EDUCATED GUESS, expert judgment (no public doctrinal distance)
      hq_casualty_modifier: 0.10,    // NS-01: moved from HQ_CASUALTY_MODIFIERS.NATO.corps
      divisions: 3,
      defaultName: 'Corps',
      combat_ratio: 0.35
    },

    // DEAD-002: nation-specific transport planning factors
    transport_standards: {
      min_dos_medical:       7,   // STANAG 2143 / AJMedP-1 baseline
      convoy_interval_days:  3,
      max_convoy_trucks:    10,
      escort_required_above: 5,
    },

    // DEAD-006: national medical supply DOS thresholds for generateSupplyWarnings()
    medical_logistics: {
      minimum_mdos_days:     7,   // STANAG 2143 required forward stock
      warning_dos_threshold: 10,  // NATO standard early-warning threshold
    },

    notes: 'NATO heavy brigade with full organic support. Balanced HQ positioning reflects standard NATO doctrine for force protection and C2 effectiveness.'
  },

  US: {
    id: 'US',
    flag: FLAG_ICONS.USA,
    label: 'US Army Standard',
    shortLabel: 'US',
    icon: FLAG_ICONS.USA,

    // NS-02: Rear area security modifier
    rear_area_security_modifier: 0.80,
    rear_area_security_description: 'US rear area security operations (RASO) doctrine provides comprehensive protection',

    // v2.2 CSS-004: Available brigade types
    // BUG-074 FIX: STRYKER added — was missing due to stale comment claiming key didn't exist.
    // STRYKER was added to UNIT_TYPES in unitTypes.js v2.5.0 (BUG-016) but never added here.
    available_brigade_types: [
      'HEAVY',          // Armored Brigade Combat Team (ABCT)
      'INFANTRY',       // Infantry Brigade Combat Team (IBCT)
      'MECHANIZED',     // Retained — used in NATO-US interoperability contexts
      'STRYKER',        // Stryker Brigade Combat Team (SBCT) — BUG-074 FIX
      'AIRBORNE',
      'AIR_ASSAULT',
      'ARTILLERY',
      'AVIATION',
      'LOGISTICS',      // 2026-10-25: the US sustainment brigade is the logistics brigade (alias, label 'Sustainment Brigade')
      'ENGINEERS',      // 2026-10-24: engineer brigade (3 engineer battalions, support, HQ)
      'MEDICAL'
    ],
    css_default: 'LOGISTICS',

    battalion: {
      strength: 800,
      hq_personnel: 100,
      breakdown_ratios: {
        command: 0.50,
        support: 0.50
      },
      hq_position_feba_km: 12,
      hq_casualty_modifier: 0.28,    // NS-01: moved from HQ_CASUALTY_MODIFIERS.US.battalion
      defaultName: 'Battalion',
      combat_ratio: 0.70
    },
    brigade: {
      strength: 4500,
      hq_personnel: 200,
      breakdown_ratios: {
        command: 1.00,
        support: 0.00
      },
      hq_position_feba_km: 20,
      hq_casualty_modifier: 0.18,    // NS-01: moved from HQ_CASUALTY_MODIFIERS.US.brigade
      support_ratio: 0.16,           // BCT Brigade Support Battalion (BSB) ~700/4500
      battalions: 3,
      defaultName: 'Brigade Combat Team',
      combat_ratio: 0.60
    },
    division: {
      strength: 14000,
      hq_personnel: 1200,
      breakdown_ratios: {
        command: 0.15,
        support: 0.85
      },
      hq_position_feba_km: 50,   // 2026-10-25 (Dr. Tatar): division headquarters further back; EDUCATED GUESS, expert judgment (no public doctrinal distance)
      hq_casualty_modifier: 0.12,    // NS-01: moved from HQ_CASUALTY_MODIFIERS.US.division
      brigades: 3,
      defaultName: 'Division',
      combat_ratio: 0.50
    },
    corps: {
      strength: 42000,
      hq_personnel: 2500,
      breakdown_ratios: {
        command: 0.10,
        support: 0.90
      },
      hq_position_feba_km: 90,   // 2026-10-25 (Dr. Tatar): corps headquarters further back; EDUCATED GUESS, expert judgment (no public doctrinal distance)
      hq_casualty_modifier: 0.08,    // NS-01: moved from HQ_CASUALTY_MODIFIERS.US.corps
      divisions: 3,
      defaultName: 'Corps',
      combat_ratio: 0.40
    },

    // DEAD-002: nation-specific transport planning factors
    transport_standards: {
      min_dos_medical:       5,   // ADP 4-0 push-logistics: shorter cycle, higher frequency
      convoy_interval_days:  2,   // US push-logistics: more frequent resupply runs
      max_convoy_trucks:    15,   // larger convoys with integrated security elements
      escort_required_above: 6,
    },

    // DEAD-006: national medical supply DOS thresholds for generateSupplyWarnings()
    medical_logistics: {
      minimum_mdos_days:     5,   // ADP 4-0 push-logistics — lower forward stock, higher frequency
      warning_dos_threshold: 7,   // earlier resupply trigger to maintain push cadence
    },

    notes: 'US ABCT/IBCT modular structure. Conservative HQ positioning prioritizes force protection against modern artillery ranges (30–40 km+).'
  },

  MULTINATIONAL: {
    id: 'MULTINATIONAL',
    flag: FLAG_ICONS.MULTINATIONAL,
    label: 'Multinational Force',
    shortLabel: 'MN',
    icon: FLAG_ICONS.MULTINATIONAL,

    // NS-02: Rear area security modifier
    rear_area_security_modifier: 0.97,
    rear_area_security_description: 'Average multinational rear area protection',

    // v2.2 CSS-004: Multinational allows ALL brigade types
    // BUG-074 FIX: STRYKER added — US SBCTs participate in multinational forces.
    available_brigade_types: [
      'HEAVY',
      'INFANTRY',
      'MECHANIZED',
      'STRYKER',        // BUG-074 FIX: US SBCT in multinational context
      'AIRBORNE',
      'AIR_ASSAULT',
      'MOUNTAIN',
      'ARTILLERY',
      'AVIATION',
      'LOGISTICS',
      'ENGINEERS',      // 2026-10-24: engineer brigade (3 engineer battalions, support, HQ)
      'MEDICAL'
    ],
    css_default: 'LOGISTICS',

    battalion: {
      strength: 750,
      hq_personnel: 100,
      breakdown_ratios: {
        command: 0.50,
        support: 0.50
      },
      hq_position_feba_km: 10,
      hq_casualty_modifier: 0.30,    // NS-01: moved from HQ_CASUALTY_MODIFIERS.MULTINATIONAL.battalion
      defaultName: 'Battalion',
      combat_ratio: 0.70
    },
    brigade: {
      strength: 5000,
      hq_personnel: 200,
      breakdown_ratios: {
        command: 1.00,
        support: 0.00
      },
      hq_position_feba_km: 18,
      hq_casualty_modifier: 0.20,    // NS-01: moved from HQ_CASUALTY_MODIFIERS.MULTINATIONAL.brigade
      support_ratio: 0.16,           // NATO baseline — lead-nation standard applies
      battalions: 3,
      defaultName: 'Brigade',
      combat_ratio: 0.55
    },
    division: {
      strength: 15000,
      hq_personnel: 950,
      breakdown_ratios: {
        command: 0.15,
        support: 0.85
      },
      hq_position_feba_km: 50,   // 2026-10-25 (Dr. Tatar): division headquarters further back; EDUCATED GUESS, expert judgment (no public doctrinal distance)
      hq_casualty_modifier: 0.15,    // NS-01: moved from HQ_CASUALTY_MODIFIERS.MULTINATIONAL.division
      brigades: 3,
      defaultName: 'Division',
      combat_ratio: 0.45
    },
    corps: {
      strength: 45000,
      hq_personnel: 2100,
      breakdown_ratios: {
        command: 0.10,
        support: 0.90
      },
      hq_position_feba_km: 90,   // 2026-10-25 (Dr. Tatar): corps headquarters further back; EDUCATED GUESS, expert judgment (no public doctrinal distance)
      hq_casualty_modifier: 0.10,    // NS-01: moved from HQ_CASUALTY_MODIFIERS.MULTINATIONAL.corps
      divisions: 3,
      defaultName: 'Corps',
      combat_ratio: 0.38
    },

    // DEAD-002: nation-specific transport planning factors
    transport_standards: {
      min_dos_medical:       7,   // NATO baseline (coalition default)
      convoy_interval_days:  3,
      max_convoy_trucks:    10,
      escort_required_above: 5,
    },

    // DEAD-006: national medical supply DOS thresholds for generateSupplyWarnings()
    medical_logistics: {
      minimum_mdos_days:     7,   // NATO coalition default
      warning_dos_threshold: 10,
    },

    notes: 'Average across NATO nations. HQ positioning uses NATO standard balanced doctrine suitable for coalition operations.'
  },

  // NS-13: GERMAN (Bundeswehr Heer) — Heeresstruktur 5 post-2016 reform
  GERMAN: {
    id: 'GERMAN',
    flag: FLAG_ICONS.GERMANY,
    label: 'Bundeswehr Heer Standard',
    shortLabel: 'DEU',
    icon: FLAG_ICONS.GERMANY,

    // NS-02 pattern: rear area security
    rear_area_security_modifier: 0.90,
    rear_area_security_description: 'Heimatschutz integration provides additional rear area security depth',

    // Bundeswehr brigade types: Panzer, Panzergrenadier, Jäger, Gebirgsjäger, Fallschirmjäger
    available_brigade_types: [
      'HEAVY',          // Panzerbrigade (Armored)
      'MECHANIZED',     // Panzergrenadierbrigade
      'INFANTRY',       // Jägerbrigade
      'AIRBORNE',       // Fallschirmjägerbrigade (1st Airborne)
      'MOUNTAIN',       // Gebirgsjägerbrigade (Bavaria / Alps)
      'ARTILLERY',
      'LOGISTICS',
      'ENGINEERS',      // 2026-10-24: engineer brigade (3 engineer battalions, support, HQ)
      'MEDICAL'
    ],
    css_default: 'LOGISTICS',

    battalion: {
      strength: 1000,
      hq_personnel: 150,
      // Bundeswehr Stab has dedicated S-sections and an integral Stabskompanie
      breakdown_ratios: {
        command: 0.50,     // 75 — command staff (S1–S6, CO, XO, CSM)
        support: 0.50      // 75 — Stabskompanie (communications, admin support)
      },
      hq_position_feba_km: 10,
      hq_casualty_modifier: 0.28,
      defaultName: 'Battalion',  // 2026-10-25 English UI default (as BUG-NAME-001 for Romania); the national term is a label, see unitTypeLabels.js
      combat_ratio: 0.70
    },
    brigade: {
      strength: 4800,
      hq_personnel: 200,
      breakdown_ratios: {
        command: 1.00,     // Brigade Stab — pure command; support provided by Versorgungsbataillon
        support: 0.00
      },
      hq_position_feba_km: 18,
      hq_casualty_modifier: 0.20,
      support_ratio: 0.15,           // Versorgungsbataillon — slightly leaner CSS (~720/4800)
      battalions: 3,
      defaultName: 'Brigade',
      combat_ratio: 0.55
    },
    division: {
      strength: 22000,
      hq_personnel: 800,
      breakdown_ratios: {
        command: 0.14,     // 112 — Divisionskommandeur, G-staff, TOC
        support: 0.86      // 688 — Divisionsnachrichtenbataillon, Sanitätspersonal, Logistik
      },
      hq_position_feba_km: 50,   // 2026-10-25 (Dr. Tatar): division headquarters further back; EDUCATED GUESS, expert judgment (no public doctrinal distance)
      hq_casualty_modifier: 0.14,
      brigades: 3,
      defaultName: 'Division',
      combat_ratio: 0.45
    },
    corps: {
      strength: 65000,
      hq_personnel: 1800,
      breakdown_ratios: {
        command: 0.09,     // 162 — commanding general, chief of staff, J/G staff branches
        support: 0.91      // 1638 — signal, medical service, logistics command, Fernmeldedienst
      },
      hq_position_feba_km: 90,   // 2026-10-25 (Dr. Tatar): corps headquarters further back; EDUCATED GUESS, expert judgment (no public doctrinal distance)
      hq_casualty_modifier: 0.09,
      divisions: 3,
      defaultName: 'Corps',  // 2026-10-25 English UI default (as BUG-NAME-001 for Romania); the national term is a label, see unitTypeLabels.js
      combat_ratio: 0.35
    },

    // DEAD-002: nation-specific transport planning factors
    transport_standards: {
      min_dos_medical:       7,   // Bundeswehr AtN doctrine — NATO-aligned 7 DOS
      convoy_interval_days:  3,
      max_convoy_trucks:    10,
      escort_required_above: 5,
    },

    // DEAD-006: national medical supply DOS thresholds for generateSupplyWarnings()
    medical_logistics: {
      minimum_mdos_days:     7,   // Bundeswehr logistics doctrine — NATO aligned
      warning_dos_threshold: 10,
    },

    notes: 'Bundeswehr Heer — Panzer/Panzergrenadier structure. Battalions larger than NATO standard (1000 vs 800) reflecting full Stabskompanie. Heimatschutz territorial defense integration provides additional rear area depth. 1st Airborne Division (Luftlandedivision) for airborne operations.'
  },

  // NS-14: BRITISH (British Army) — battlegroup-centric doctrine
  BRITISH: {
    id: 'BRITISH',
    flag: FLAG_ICONS.UK,
    label: 'British Army Standard',
    shortLabel: 'GBR',
    icon: FLAG_ICONS.UK,

    // NS-02 pattern: rear area security
    rear_area_security_modifier: 0.92,
    rear_area_security_description: 'Royal Military Police provost support provides rear area security',

    // British Army brigade types: Armoured Infantry, Strike, Air Assault, etc.
    available_brigade_types: [
      'HEAVY',          // Armoured Infantry Brigade
      'MECHANIZED',     // Strike Brigade (Ajax-equipped)
      'INFANTRY',       // Light Role Infantry Brigade
      'AIRBORNE',       // 16 Air Assault Brigade Combat Team
      'AVIATION',       // Joint Helicopter Command elements
      'ARTILLERY',
      'LOGISTICS',
      'ENGINEERS',      // 2026-10-24: engineer brigade (3 engineer battalions, support, HQ)
      'MEDICAL'
    ],
    css_default: 'LOGISTICS',

    battalion: {
      strength: 750,
      hq_personnel: 120,
      // British battalion has Regimental Headquarters and integral Battle Group HQ capability
      breakdown_ratios: {
        command: 0.50,     // 60 — CO, 2IC, adjutant, RSM, staff officers
        support: 0.50      // 60 — HQ Company (signals, admin, motor transport)
      },
      hq_position_feba_km: 10,
      hq_casualty_modifier: 0.30,
      defaultName: 'Battalion',
      combat_ratio: 0.72
    },
    brigade: {
      strength: 4800,
      hq_personnel: 180,
      breakdown_ratios: {
        command: 1.00,     // Brigade HQ — pure command; CSS provided by Close Support Logistic Regiment
        support: 0.00
      },
      hq_position_feba_km: 18,
      hq_casualty_modifier: 0.20,
      support_ratio: 0.16,           // RLC Close Support Logistic Regiment (~720/4500)
      battalions: 3,
      defaultName: 'Brigade',
      combat_ratio: 0.55
    },
    division: {
      strength: 20000,
      hq_personnel: 750,
      breakdown_ratios: {
        command: 0.15,     // 113 — GOC, DCOS, G-staff branches, TOC
        support: 0.85      // 637 — signal regiment, medical regiment (RAMC), J4 staff
      },
      hq_position_feba_km: 50,   // 2026-10-25 (Dr. Tatar): division headquarters further back; EDUCATED GUESS, expert judgment (no public doctrinal distance)
      hq_casualty_modifier: 0.14,
      brigades: 3,
      defaultName: 'Division',
      combat_ratio: 0.45
    },
    corps: {
      strength: 60000,
      hq_personnel: 1600,
      breakdown_ratios: {
        command: 0.09,     // 144 — Commander Land Forces / corps CDR, chief of staff, J-staff
        support: 0.91      // 1456 — 1 Signal Brigade, Close Support Medical Group, 101 Log Bde HQ
      },
      hq_position_feba_km: 90,   // 2026-10-25 (Dr. Tatar): corps headquarters further back; EDUCATED GUESS, expert judgment (no public doctrinal distance)
      hq_casualty_modifier: 0.09,
      divisions: 3,
      defaultName: 'Corps',
      combat_ratio: 0.35
    },

    // DEAD-002: nation-specific transport planning factors
    transport_standards: {
      min_dos_medical:       5,   // JSP 886 / RLC — leaner forward stock, frequent RLC runs
      convoy_interval_days:  3,
      max_convoy_trucks:     8,
      escort_required_above: 4,
    },

    // DEAD-006: national medical supply DOS thresholds for generateSupplyWarnings()
    medical_logistics: {
      minimum_mdos_days:     5,   // JSP 886 — RLC close support, lean forward stock
      warning_dos_threshold: 7,
    },

    notes: 'British Army — battlegroup-centric doctrine; battalions formed into battlegroups by cross-attaching armour and infantry. Smaller battalions than German/US (750 vs 800–1000). 16 Air Assault Brigade Combat Team provides airborne strike capability. Royal Military Police provide close area security.'
  },

  // NS-15: POLISH (Wojska Lądowe) — WOT territorial defence integration
  POLISH: {
    id: 'POLISH',
    flag: FLAG_ICONS.POLAND,
    label: 'Wojska Lądowe Standard',
    shortLabel: 'POL',
    icon: FLAG_ICONS.POLAND,

    // NS-02 pattern: rear area security
    rear_area_security_modifier: 0.88,
    rear_area_security_description: 'Territorial Defence Force (WOT) integration provides significant rear area depth',

    // Polish Land Forces brigade types: Pancerna, Zmechanizowana, Desantowoszturmowa, Górska
    available_brigade_types: [
      'HEAVY',          // Brygada Pancerna (Armored)
      'MECHANIZED',     // Brygada Zmechanizowana
      'INFANTRY',       // Brygada Piechoty (Light Infantry)
      'AIRBORNE',       // Brygada Desantowoszturmowa (Air Assault / Airborne)
      'MOUNTAIN',       // Brygada Górska (Podhale Rifles)
      'ARTILLERY',
      'LOGISTICS',
      'ENGINEERS',      // 2026-10-24: engineer brigade (3 engineer battalions, support, HQ)
      'MEDICAL'
    ],
    css_default: 'LOGISTICS',

    battalion: {
      strength: 850,
      hq_personnel: 130,
      // Polish battalion Stab with integral Kompania Dowodzenia (command company)
      breakdown_ratios: {
        command: 0.50,     // 65 — dowódca, S-sekcje, oficerowie sztabowi
        support: 0.50      // 65 — Kompania Dowodzenia (łączność, transport, admin)
      },
      hq_position_feba_km: 8,
      hq_casualty_modifier: 0.28,
      defaultName: 'Battalion',  // 2026-10-25 English UI default (as BUG-NAME-001 for Romania); the national term is a label, see unitTypeLabels.js
      combat_ratio: 0.72
    },
    brigade: {
      strength: 4500,
      hq_personnel: 190,
      // Polish brigade has 4 maneuver battalions (vs NATO 3) — larger organic combat element
      breakdown_ratios: {
        command: 1.00,     // Sztab Brygady — pure command; Batalion Logistyczny is separate
        support: 0.00
      },
      hq_position_feba_km: 15,
      hq_casualty_modifier: 0.20,
      support_ratio: 0.18,           // 4-bn brigade, Batalion Logistyczny heavier than NATO 3-bn (~810/4500)
      battalions: 4,        // Polish doctrine: 4 maneuver battalions per brigade
      defaultName: 'Brigade',  // 2026-10-25 English UI default (as BUG-NAME-001 for Romania); the national term is a label, see unitTypeLabels.js
      combat_ratio: 0.58
    },
    division: {
      strength: 21000,
      hq_personnel: 820,
      breakdown_ratios: {
        command: 0.15,     // 123 — dowódca dywizji, G-sztab, TOC
        support: 0.85      // 697 — batalion dowodzenia, służba medyczna, batalion zabezpieczenia
      },
      hq_position_feba_km: 50,   // 2026-10-25 (Dr. Tatar): division headquarters further back; EDUCATED GUESS, expert judgment (no public doctrinal distance)
      hq_casualty_modifier: 0.14,
      brigades: 3,
      defaultName: 'Division',  // 2026-10-25 English UI default (as BUG-NAME-001 for Romania); the national term is a label, see unitTypeLabels.js
      combat_ratio: 0.46
    },
    corps: {
      strength: 62000,
      hq_personnel: 1700,
      breakdown_ratios: {
        command: 0.09,     // 153 — dowódca korpusu, szef sztabu, J/G-sztab
        support: 0.91      // 1547 — brygada dowodzenia, brygada medyczna, brygada logistyczna
      },
      hq_position_feba_km: 90,   // 2026-10-25 (Dr. Tatar): corps headquarters further back; EDUCATED GUESS, expert judgment (no public doctrinal distance)
      hq_casualty_modifier: 0.09,
      divisions: 3,
      defaultName: 'Corps',  // 2026-10-25 English UI default (as BUG-NAME-001 for Romania); the national term is a label, see unitTypeLabels.js
      combat_ratio: 0.36
    },

    // DEAD-002: nation-specific transport planning factors
    transport_standards: {
      min_dos_medical:       7,   // NATO integration — aligned with STANAG 2143
      convoy_interval_days:  3,
      max_convoy_trucks:    10,
      escort_required_above: 5,
    },

    // DEAD-006: national medical supply DOS thresholds for generateSupplyWarnings()
    medical_logistics: {
      minimum_mdos_days:     7,   // NATO integration standard
      warning_dos_threshold: 10,
    },

    notes: 'Wojska Lądowe — 4 maneuver battalions per brigade (vs NATO 3), reflecting Polish warfighting doctrine and front-line nation posture. Wojska Obrony Terytorialnej (WOT) integration provides substantial rear area security. Forward HQ positioning consistent with aggressive territorial defense posture.'
  },

  // NS-17: FRENCH (Armée de Terre) — Scorpion programme / brigade interarmes (BIA)
  FRENCH: {
    id: 'FRENCH',
    flag: FLAG_ICONS.FRANCE,
    label: 'Armée de Terre Standard',
    shortLabel: 'FRA',
    icon: FLAG_ICONS.FRANCE,

    // NS-02 pattern: rear area security
    // ALAT (Aviation Légère de l'Armée de Terre) provides air-mobile rear area security.
    // Brigade Logistique (B.LOG, created March 2024) adds dedicated rear area support.
    // No WOT-equivalent territorial force — slightly lower modifier than Polish.
    rear_area_security_modifier: 0.92,
    rear_area_security_description: 'ALAT air-mobile security + Brigade Logistique rear area support (B.LOG, 2024)',

    // Armée de Terre brigade types (Scorpion era, LPM 2024-2030):
    // Blindée = Leclerc MBT heavy armour
    // Médiane = VBCI/Griffon mechanized (the majority BIA type)
    // Légère = VBMR-L Serval / infantry
    // Also: 11e Brigade Parachutiste (airborne), 27e Brigade d'Infanterie de Montagne (mountain)
    available_brigade_types: [
      'HEAVY',          // Brigade Blindée (Leclerc XLR)
      'MECHANIZED',     // Brigade Médiane / BIA (VBCI, Griffon)
      'INFANTRY',       // Brigade Légère (Serval, VBMR-L)
      'AIRBORNE',       // 11e Brigade Parachutiste (Serval airborne variant)
      'MOUNTAIN',       // 27e Brigade d'Infanterie de Montagne
      'ARTILLERY',
      'LOGISTICS',
      'ENGINEERS',      // 2026-10-24: engineer brigade (3 engineer battalions, support, HQ)
      'MEDICAL'
    ],
    css_default: 'LOGISTICS',

    battalion: {
      // French permanent organizational unit = régiment (800–1200 pers).
      // Used as battalion analog here. GTIA (800–1500) is the tactical sub-unit.
      // Source: Programme Scorpion GTIA documentation; BIA 23 exercise data.
      strength: 950,
      hq_personnel: 130,
      // État-Major de régiment + Compagnie de Commandement et de Transmissions (CCT)
      breakdown_ratios: {
        command: 0.50,     // 65 — CO, XO, EM sections (S1–S6)
        support: 0.50      // 65 — CCT (transmissions, admin, liaison)
      },
      hq_position_feba_km: 8,
      hq_casualty_modifier: 0.28,
      defaultName: 'Battalion',  // 2026-10-25 English UI default (as BUG-NAME-001 for Romania); the national term is a label, see unitTypeLabels.js
      combat_ratio: 0.72
    },
    brigade: {
      // Scorpion BIA doctrinal target: ~5000; confirmed ~3700 at BIA 23 exercise (2023).
      // Planning value 4500 — midpoint. 3 GTIAs (régiments) per BIA is standard;
      // 4 GTIAs possible for projection (LPM 2024-2030 autonomous brigade requirement).
      // Source: opex360.com May 2023 CEMAT briefing; LPM 2024-2030 parliamentary docs.
      strength: 4500,
      hq_personnel: 180,
      // Brigade HQ = État-Major de brigade; CCT is a separate subordinate unit.
      // Brigade HQ itself is pure command, no organic support element at HQ level.
      breakdown_ratios: {
        command: 1.00,     // 180 — EM de brigade (pure command, no organic support HQ element)
        support: 0.00
      },
      hq_position_feba_km: 16,
      hq_casualty_modifier: 0.20,
      support_ratio: 0.16,           // BSL (Bataillon de Soutien Logistique) ~720/4500
      battalions: 3,        // 3 GTIAs (régiments) standard Scorpion BIA
      defaultName: 'Brigade',  // 2026-10-25 English UI default (as BUG-NAME-001 for Romania); the national term is a label, see unitTypeLabels.js
      combat_ratio: 0.57
    },
    division: {
      // Scorpion division: ~25000. 3 BIA + 3 régiments endivisionnés (génie, artillerie,
      // spécialisé) + état-major divisionnaire.
      // Source: Organisation de l'Armée de Terre française (Wikipedia, updated 2025).
      strength: 25000,
      hq_personnel: 780,
      breakdown_ratios: {
        command: 0.13,     // 101 — général de division, EM-DIV, TOC (poste de commandement tactique)
        support: 0.87      // 679 — bataillon de commandement et transmissions, services médicaux, soutien
      },
      hq_position_feba_km: 50,   // 2026-10-25 (Dr. Tatar): division headquarters further back; EDUCATED GUESS, expert judgment (no public doctrinal distance)
      hq_casualty_modifier: 0.14,
      brigades: 3,
      defaultName: 'Division',
      combat_ratio: 0.47
    },
    corps: {
      // NATO-comparable corps level. France projects a corps HQ (1er Corps)
      // capable of commanding a 2-division deployment under LPM 2024-2030.
      strength: 65000,
      hq_personnel: 1600,
      breakdown_ratios: {
        command: 0.09,     // 144 — général de corps d'armée, chef d'état-major, sections J/G
        support: 0.91      // 1456 — brigade transmissions, RMED, B.LOG, brigade génie
      },
      hq_position_feba_km: 90,   // 2026-10-25 (Dr. Tatar): corps headquarters further back; EDUCATED GUESS, expert judgment (no public doctrinal distance)
      hq_casualty_modifier: 0.09,
      divisions: 3,
      defaultName: 'Corps',  // 2026-10-25 English UI default (as BUG-NAME-001 for Romania); the national term is a label, see unitTypeLabels.js
      combat_ratio: 0.37
    },

    // DEAD-002: nation-specific transport planning factors
    transport_standards: {
      min_dos_medical:       5,   // AJMedP-1 / Scorpion doctrine — forward leanness
      convoy_interval_days:  3,
      max_convoy_trucks:     8,
      escort_required_above: 4,
    },

    // DEAD-006: national medical supply DOS thresholds for generateSupplyWarnings()
    medical_logistics: {
      minimum_mdos_days:     5,   // AJMedP-1 / Scorpion lean-forward doctrine
      warning_dos_threshold: 7,
    },

    notes: 'Armée de Terre — Scorpion-era brigade interarmes (BIA). 3 régiments (GTIAs) per brigade standard; 4-GTIA capable for autonomous projection (LPM 2024-2030). Leclerc XLR for heavy brigades; VBCI + Griffon for mechanized brigades. ALAT (NH90 Caïman) provides organic Army MEDEVAC — not Air Force. Brigade Logistique (B.LOG) created March 2024 for divisional sustainment. No territorial defence force equivalent to WOT; ALAT provides air-mobile rear security.'
  }
};

// ============================================================================
// MTF POSITIONS FROM FEBA (km) — NATO Standards + NS-13/14/15 additions
// ============================================================================

export const MTF_POSITIONS = {
  ROMANIAN: {
    role_1: 8,
    role_2: 20,
    role_3: 100
  },
  NATO: {
    role_1: 10,
    role_2: 25,
    role_3: 120
  },
  US: {
    role_1: 12,
    role_2: 30,
    role_3: 150
  },
  MULTINATIONAL: {
    role_1: 10,
    role_2: 25,
    role_3: 120
  },
  // NS-13: Bundeswehr MTF positioning — doctrine similar to NATO with slightly longer Role 3 standoff
  GERMAN: {
    role_1: 8,
    role_2: 20,
    role_3: 110
  },
  // NS-14: British Army MTF positioning — slightly deeper Role 2 (Field Hospital positioning doctrine)
  BRITISH: {
    role_1: 8,
    role_2: 22,
    role_3: 110
  },
  // NS-15: Polish MTF positioning — forward orientation matching HQ doctrine
  POLISH: {
    role_1: 7,
    role_2: 18,
    role_3: 100
  },
  // NS-17: French Army MTF positioning — NATO-equivalent; Role 2 marginally closer
  // per Groupement Médico-Chirurgical (GMC) forward-positioning doctrine.
  // Role 3 similar to German/British — corps rear, accessed via French military airfield.
  // Source: No French-specific MTF doctrinal standoff found; research-derived from NATO baseline.
  FRENCH: {
    role_1: 10,
    role_2: 24,
    role_3: 110
  }
};

// ============================================================================
// SUPPORT UNITS — Corps/Division-level CSS assets by nation
// ============================================================================

export const SUPPORT_UNITS = {
  ROMANIAN: {
    medical_brigade: {
      strength: 800,
      echelon: 'corps',
      provides: ['role_2', 'role_3'],
      icon: MILITARY_ICONS.MEDICAL
    },
    logistics_brigade: {
      strength: 1200,
      echelon: 'corps',
      provides: ['supply', 'maintenance'],
      icon: MILITARY_ICONS.LOGISTICS
    }
  },
  NATO: {
    medical_brigade: {
      strength: 1200,
      echelon: 'corps',
      provides: ['role_2', 'role_3', 'role_4'],
      icon: MILITARY_ICONS.MEDICAL
    },
    logistics_brigade: {
      strength: 2000,
      echelon: 'corps',
      provides: ['supply', 'maintenance', 'transport'],
      icon: MILITARY_ICONS.LOGISTICS
    }
  },
  US: {
    medical_brigade: {
      strength: 1500,
      echelon: 'corps',
      provides: ['role_2', 'role_3', 'role_4'],
      icon: MILITARY_ICONS.MEDICAL
    },
    sustainment_brigade: {
      strength: 3000,
      echelon: 'division',
      provides: ['supply', 'maintenance', 'transport'],
      icon: MILITARY_ICONS.LOGISTICS
    }
  },
  MULTINATIONAL: {
    medical_brigade: {
      strength: 1000,
      echelon: 'corps',
      provides: ['role_2', 'role_3'],
      icon: MILITARY_ICONS.MEDICAL
    },
    logistics_brigade: {
      strength: 1500,
      echelon: 'corps',
      provides: ['supply', 'maintenance'],
      icon: MILITARY_ICONS.LOGISTICS
    }
  },
  // NS-13: Bundeswehr — Sanitätsregiment (Medical Service) + Versorgungsregiment (Supply Regt)
  GERMAN: {
    sanitaetsregiment: {
      strength: 1000,
      echelon: 'corps',
      provides: ['role_2', 'role_3'],
      icon: MILITARY_ICONS.MEDICAL
    },
    versorgungsregiment: {
      strength: 2200,
      echelon: 'corps',
      provides: ['supply', 'maintenance', 'transport'],
      icon: MILITARY_ICONS.LOGISTICS
    }
  },
  // NS-14: British Army — Close Support Medical Group + 101 Logistic Brigade
  BRITISH: {
    close_support_medical_group: {
      strength: 900,
      echelon: 'corps',
      provides: ['role_2', 'role_3'],
      icon: MILITARY_ICONS.MEDICAL
    },
    logistic_brigade: {
      strength: 1800,
      echelon: 'corps',
      provides: ['supply', 'maintenance', 'transport'],
      icon: MILITARY_ICONS.LOGISTICS
    }
  },
  // NS-15: Wojska Lądowe — Brygada Medyczna + Brygada Logistyczna
  POLISH: {
    brygada_medyczna: {
      strength: 900,
      echelon: 'corps',
      provides: ['role_2', 'role_3'],
      icon: MILITARY_ICONS.MEDICAL
    },
    brygada_logistyczna: {
      strength: 1800,
      echelon: 'corps',
      provides: ['supply', 'maintenance', 'transport'],
      icon: MILITARY_ICONS.LOGISTICS
    }
  },
  // NS-17: Armée de Terre — Régiment Médical (RMED) + Brigade Logistique (B.LOG)
  // RMED based in Valbonne; provides Role 3 medical support.
  // B.LOG created March 26, 2024 (Invalides ceremony); 8 Train regiments; ~7000 active.
  // Designed to sustain 2 French brigades + 1 allied brigade at division level by 2027.
  // Source: defense.gouv.fr, March 2024 B.LOG activation announcement.
  FRENCH: {
    regiment_medical: {
      strength: 900,
      echelon: 'corps',
      provides: ['role_2', 'role_3'],
      icon: MILITARY_ICONS.MEDICAL
    },
    brigade_logistique: {
      strength: 2000,
      echelon: 'corps',
      provides: ['supply', 'maintenance', 'transport'],
      icon: MILITARY_ICONS.LOGISTICS
    }
  }
};

// ============================================================================
// SUPPORT UNITS BY ECHELON — Detailed organic unit breakdown
// ============================================================================

export const SUPPORT_UNITS_BY_ECHELON = {
  ROMANIAN: {
    battalion: {
      signal_platoon: { strength: 25, provides: ['communications'] },
      medical_platoon: { strength: 30, provides: ['role_1'] },
      supply_section: { strength: 15, provides: ['class_i', 'class_v'] }
    },
    brigade: {
      signal_company: { strength: 80, provides: ['communications', 'cyber'] },
      medical_company: { strength: 120, provides: ['role_2_basic'] },
      logistics_battalion: { strength: 350, provides: ['supply', 'maintenance'] },
      engineer_company: { strength: 100, provides: ['mobility', 'counter_mobility'] },
      artillery_battalion: { strength: 400, provides: ['fire_support'] },
      reconnaissance_company: { strength: 80, provides: ['intelligence'] }
    },
    division: {
      signal_battalion: { strength: 300, provides: ['communications', 'cyber', 'ew'] },
      medical_battalion: { strength: 400, provides: ['role_2_enhanced'] },
      logistics_brigade: { strength: 1200, provides: ['supply', 'maintenance', 'transport'] },
      engineer_battalion: { strength: 450, provides: ['mobility', 'construction'] },
      artillery_brigade: { strength: 1500, provides: ['fire_support', 'mlrs'] },
      air_defense_battalion: { strength: 350, provides: ['shorad'] }
    },
    corps: {
      signal_brigade: { strength: 800, provides: ['strategic_comms', 'cyber'] },
      medical_brigade: { strength: 800, provides: ['role_3'] },
      logistics_command: { strength: 2500, provides: ['theater_logistics'] },
      engineer_brigade: { strength: 1200, provides: ['construction', 'route_clearance'] },
      aviation_brigade: { strength: 1000, provides: ['attack', 'lift', 'medevac'] }
    }
  },
  NATO: {
    battalion: {
      signal_platoon: { strength: 30, provides: ['communications'] },
      medical_platoon: { strength: 35, provides: ['role_1'] },
      supply_section: { strength: 20, provides: ['class_i', 'class_v'] }
    },
    brigade: {
      signal_company: { strength: 120, provides: ['communications', 'cyber'] },
      medical_company: { strength: 150, provides: ['role_2_basic'] },
      logistics_battalion: { strength: 500, provides: ['supply', 'maintenance'] },
      engineer_company: { strength: 150, provides: ['mobility', 'counter_mobility'] },
      artillery_battalion: { strength: 500, provides: ['fire_support'] },
      reconnaissance_squadron: { strength: 120, provides: ['intelligence'] }
    },
    division: {
      signal_battalion: { strength: 400, provides: ['communications', 'cyber', 'ew'] },
      medical_battalion: { strength: 600, provides: ['role_2_enhanced'] },
      sustainment_brigade: { strength: 2000, provides: ['supply', 'maintenance', 'transport'] },
      engineer_battalion: { strength: 600, provides: ['mobility', 'construction'] },
      fires_brigade: { strength: 2000, provides: ['fire_support', 'mlrs', 'atacms'] },
      air_defense_battalion: { strength: 500, provides: ['shorad', 'c_uas'] }
    },
    corps: {
      signal_brigade: { strength: 1200, provides: ['strategic_comms', 'cyber', 'space'] },
      medical_brigade: { strength: 1200, provides: ['role_3', 'role_4'] },
      theater_sustainment: { strength: 4000, provides: ['theater_logistics'] },
      engineer_brigade: { strength: 2000, provides: ['construction', 'route_clearance'] },
      aviation_brigade: { strength: 2500, provides: ['attack', 'lift', 'medevac'] }
    }
  },
  US: {
    battalion: {
      signal_platoon: { strength: 35, provides: ['communications'] },
      medical_platoon: { strength: 40, provides: ['role_1'] },
      fsc: { strength: 100, provides: ['class_i', 'class_iii', 'class_v', 'maintenance'] }
    },
    brigade: {
      signal_company: { strength: 150, provides: ['communications', 'cyber'] },
      bsb: { strength: 600, provides: ['role_2_basic', 'supply', 'maintenance'] },
      engineer_company: { strength: 180, provides: ['mobility', 'counter_mobility'] },
      fires_battalion: { strength: 500, provides: ['fire_support'] },
      cavalry_squadron: { strength: 400, provides: ['reconnaissance', 'security'] }
    },
    division: {
      signal_battalion: { strength: 500, provides: ['communications', 'cyber', 'ew'] },
      medical_battalion: { strength: 700, provides: ['role_2_enhanced'] },
      sustainment_brigade: { strength: 3000, provides: ['supply', 'maintenance', 'transport'] },
      engineer_brigade: { strength: 1500, provides: ['mobility', 'construction'] },
      divarty: { strength: 2200, provides: ['fire_support', 'mlrs'] },
      air_defense_battalion: { strength: 600, provides: ['shorad', 'c_uas'] },
      cab: { strength: 2800, provides: ['attack', 'assault', 'medevac', 'gsab'] }
    },
    corps: {
      signal_brigade: { strength: 1500, provides: ['strategic_comms', 'cyber', 'space'] },
      medical_brigade: { strength: 1500, provides: ['role_3', 'role_4'] },
      tsc: { strength: 5000, provides: ['theater_logistics'] },
      engineer_brigade: { strength: 2500, provides: ['construction', 'route_clearance'] },
      aviation_command: { strength: 4000, provides: ['attack', 'lift', 'medevac'] }
    }
  },
  MULTINATIONAL: {
    battalion: {
      signal_platoon: { strength: 28, provides: ['communications'] },
      medical_platoon: { strength: 33, provides: ['role_1'] },
      supply_section: { strength: 18, provides: ['class_i', 'class_v'] }
    },
    brigade: {
      signal_company: { strength: 100, provides: ['communications'] },
      medical_company: { strength: 130, provides: ['role_2_basic'] },
      logistics_battalion: { strength: 420, provides: ['supply', 'maintenance'] },
      engineer_company: { strength: 120, provides: ['mobility'] },
      artillery_battalion: { strength: 450, provides: ['fire_support'] }
    },
    division: {
      signal_battalion: { strength: 350, provides: ['communications', 'cyber'] },
      medical_battalion: { strength: 500, provides: ['role_2_enhanced'] },
      logistics_brigade: { strength: 1600, provides: ['supply', 'maintenance', 'transport'] },
      engineer_battalion: { strength: 520, provides: ['mobility', 'construction'] },
      artillery_brigade: { strength: 1700, provides: ['fire_support'] }
    },
    corps: {
      signal_brigade: { strength: 1000, provides: ['strategic_comms', 'cyber'] },
      medical_brigade: { strength: 1000, provides: ['role_3'] },
      logistics_command: { strength: 3200, provides: ['theater_logistics'] },
      engineer_brigade: { strength: 1600, provides: ['construction'] },
      // NS-16: Added — averaged from RO(1000)/NATO(2500)/US(4000)/DEU(2200)/GBR(2100)/POL(1800).
      // In a MNC, aviation is contributed by framework/lead nation. This is a planning average.
      // ENH-018 (Lead Nation concept) will replace this with framework-nation-specific value.
      aviation_command: { strength: 2200, provides: ['attack', 'lift', 'medevac'] }
    }
  },

  // NS-13: Bundeswehr Heer — German doctrinal unit names throughout
  GERMAN: {
    battalion: {
      // Fernmeldezug = signal platoon (Fernmelde = telecommunications)
      fernmeldezug: { strength: 30, provides: ['communications'] },
      // Sanitätszug = medical platoon (Sanitätsdienst = medical service)
      sanitaetszug: { strength: 35, provides: ['role_1'] },
      // Versorgungszug = supply platoon (Versorgung = supply/sustainment)
      versorgungszug: { strength: 20, provides: ['class_i', 'class_v'] }
    },
    brigade: {
      // Fernmeldekompanie = signal company
      fernmeldekompanie: { strength: 120, provides: ['communications', 'cyber'] },
      // Sanitätskompanie = medical company (Bundeswehr Sanitätsdienst provides)
      sanitaetskompanie: { strength: 140, provides: ['role_2_basic'] },
      // Versorgungsbataillon = supply/logistics battalion (organic to brigade)
      versorgungsbataillon: { strength: 480, provides: ['supply', 'maintenance'] },
      // Pionierkompanie = engineer company (Pioniere = combat engineers)
      pionierkompanie: { strength: 150, provides: ['mobility', 'counter_mobility'] },
      // Artilleriebataillon = artillery battalion (Panzerhaubitzen 2000)
      artilleriebataillon: { strength: 500, provides: ['fire_support'] },
      // Aufklärungskompanie = reconnaissance company (Fennek, Puma-based recce)
      aufklaerungskompanie: { strength: 110, provides: ['intelligence'] }
    },
    division: {
      // Fernmeldebataillon = signal battalion
      fernmeldebataillon: { strength: 380, provides: ['communications', 'cyber', 'ew'] },
      // Sanitätsbataillon = medical battalion (Role 2 Enhanced)
      sanitaetsbataillon: { strength: 580, provides: ['role_2_enhanced'] },
      // Logistikbataillon = logistics battalion (division level; Nachschubbataillon in older doctrine)
      logistikbataillon: { strength: 1800, provides: ['supply', 'maintenance', 'transport'] },
      // Pionierbataillon = engineer battalion
      pionierbataillon: { strength: 560, provides: ['mobility', 'construction'] },
      // Artillerieregiment = artillery regiment (division-level fires; MARS MLRS)
      artillerieregiment: { strength: 1800, provides: ['fire_support', 'mlrs'] },
      // Heeresflugabwehrbataillon = army air defense battalion (Gepard successor, Skyranger)
      heeresflugabwehrbataillon: { strength: 480, provides: ['shorad', 'c_uas'] }
    },
    corps: {
      // Fernmelderegiment = signal regiment (corps level)
      fernmelderegiment: { strength: 1100, provides: ['strategic_comms', 'cyber', 'ew'] },
      // Sanitätsregiment = medical regiment (Bundeswehr Sanitätsdienst, Role 3)
      sanitaetsregiment: { strength: 1000, provides: ['role_3'] },
      // Versorgungsregiment = supply regiment (corps-level theater logistics)
      versorgungsregiment: { strength: 3500, provides: ['theater_logistics'] },
      // Pionierregiment = engineer regiment (corps)
      pionierregiment: { strength: 1800, provides: ['construction', 'route_clearance'] },
      // Heeresflieger = Army Aviation (Tigre, NH90 MEDEVAC, CH-53 lift)
      heeresflieger: { strength: 2200, provides: ['attack', 'lift', 'medevac'] }
    }
  },

  // NS-14: British Army — RAMC, RLC, Royal Signals doctrinal names
  BRITISH: {
    battalion: {
      // Royal Signals detachment embedded at battalion (Regimental Signal Officer + team)
      royal_signals_detachment: { strength: 28, provides: ['communications'] },
      // RAMC Regimental Aid Post (RAP) — battalion organic, commanded by Regimental Medical Officer
      regimental_aid_post: { strength: 32, provides: ['role_1'] },
      // RLC attached supply section for battalion quartermaster
      rlc_supply_section: { strength: 18, provides: ['class_i', 'class_v'] }
    },
    brigade: {
      // Royal Signals squadron — brigade communications and C2
      royal_signals_squadron: { strength: 110, provides: ['communications', 'cyber'] },
      // RAMC Field Ambulance — brigade-level Role 2 Basic (200–350 personnel historically)
      field_ambulance: { strength: 145, provides: ['role_2_basic'] },
      // RLC Close Support Logistic Regiment — brigade CSS (supply, transport, petroleum)
      close_support_logistic_regiment: { strength: 520, provides: ['supply', 'maintenance'] },
      // Royal Engineers close support squadron
      royal_engineers_squadron: { strength: 140, provides: ['mobility', 'counter_mobility'] },
      // Royal Artillery regiment (AS90 / Archer-equipped)
      royal_artillery_regiment: { strength: 480, provides: ['fire_support'] },
      // Household Cavalry / Royal Armoured Corps recce regiment (Formation Reconnaissance)
      formation_recce_regiment: { strength: 380, provides: ['intelligence', 'security'] }
    },
    division: {
      // 1 Signal Brigade provides signal regiment to division
      signal_regiment: { strength: 420, provides: ['communications', 'cyber', 'ew'] },
      // RAMC Medical Regiment (Role 2 Enhanced; e.g. 16 Medical Regiment for 3 Div)
      medical_regiment: { strength: 620, provides: ['role_2_enhanced'] },
      // RLC Force Support Logistic Regiment (division sustainment)
      force_support_logistic_regiment: { strength: 1900, provides: ['supply', 'maintenance', 'transport'] },
      // Royal Engineers regiment (mobility, survivability, construction)
      royal_engineers_regiment: { strength: 580, provides: ['mobility', 'construction'] },
      // Royal Artillery Multiple Launch Rocket System (M270 MLRS / M270B1)
      mlrs_regiment: { strength: 1600, provides: ['fire_support', 'mlrs'] },
      // Royal Artillery Air Defence (Sky Sabre / Starstreak)
      air_defence_regiment: { strength: 480, provides: ['shorad', 'c_uas'] }
    },
    corps: {
      // 1 Signal Brigade (corps-level strategic communications)
      signal_brigade: { strength: 1100, provides: ['strategic_comms', 'cyber', 'space'] },
      // Close Support Medical Group (RAMC / Joint Medical Group — Role 3)
      close_support_medical_group: { strength: 900, provides: ['role_3', 'role_4'] },
      // 101 Operational Sustainment Brigade (RLC — theatre-level logistics)
      operational_sustainment_brigade_101: { strength: 3800, provides: ['theater_logistics'] },
      // Royal Engineers Brigade (construction, route clearance, breaching)
      royal_engineers_brigade: { strength: 1900, provides: ['construction', 'route_clearance'] },
      // Army Air Corps regiment (Apache AH-64, Wildcat, MEDEVAC Puma)
      army_air_corps_regiment: { strength: 2100, provides: ['attack', 'lift', 'medevac'] }
    }
  },

  // NS-15: Wojska Lądowe — Polish military terminology throughout
  POLISH: {
    battalion: {
      // Pluton łączności = signal platoon
      pluton_lacznosci: { strength: 28, provides: ['communications'] },
      // Pluton medyczny = medical platoon
      pluton_medyczny: { strength: 32, provides: ['role_1'] },
      // Sekcja zaopatrzenia = supply section
      sekcja_zaopatrzenia: { strength: 16, provides: ['class_i', 'class_v'] }
    },
    brigade: {
      // Kompania łączności = signal company
      kompania_lacznosci: { strength: 105, provides: ['communications', 'cyber'] },
      // Kompania medyczna = medical company (Role 2 Basic)
      kompania_medyczna: { strength: 130, provides: ['role_2_basic'] },
      // Batalion logistyczny = logistics battalion (organic to brigade; 4-bn Polish brigades carry heavier CSS)
      batalion_logistyczny: { strength: 460, provides: ['supply', 'maintenance'] },
      // Kompania saperska = engineer company (Saperzy = combat engineers)
      kompania_saperska: { strength: 130, provides: ['mobility', 'counter_mobility'] },
      // Dywizjon artylerii = artillery battalion (Krab wheeled SPH, WR-40 Langusta MLRS)
      dywizjon_artylerii: { strength: 480, provides: ['fire_support'] },
      // Szwadron rozpoznawczy = reconnaissance squadron (Rosomak-based)
      szwadron_rozpoznawczy: { strength: 100, provides: ['intelligence'] }
    },
    division: {
      // Batalion dowodzenia i łączności = command and signal battalion
      batalion_dowodzenia_i_lacznosci: { strength: 360, provides: ['communications', 'cyber', 'ew'] },
      // Batalion medyczny = medical battalion (Role 2 Enhanced)
      batalion_medyczny: { strength: 540, provides: ['role_2_enhanced'] },
      // Pułk logistyczny = logistics regiment (division-level; Pułk = regiment)
      pulk_logistyczny: { strength: 1650, provides: ['supply', 'maintenance', 'transport'] },
      // Batalion saperski = engineer battalion
      batalion_saperski: { strength: 500, provides: ['mobility', 'construction'] },
      // Pułk artylerii = artillery regiment (Krab, WR-40, Homar-K MLRS)
      pulk_artylerii: { strength: 1700, provides: ['fire_support', 'mlrs'] },
      // Dywizjon obrony przeciwlotniczej = air defense battalion (Piorun MANPADS, SHORAD)
      dywizjon_obrony_przeciwlotniczej: { strength: 420, provides: ['shorad', 'c_uas'] }
    },
    corps: {
      // Brygada dowodzenia = command and signal brigade (corps C2)
      brygada_dowodzenia: { strength: 980, provides: ['strategic_comms', 'cyber'] },
      // Brygada medyczna = medical brigade (Role 3; Wojskowa Służba Zdrowia)
      brygada_medyczna: { strength: 900, provides: ['role_3'] },
      // Brygada logistyczna = logistics brigade (Brygada Logistyczna — corps theater logistics)
      brygada_logistyczna: { strength: 3100, provides: ['theater_logistics'] },
      // Brygada saperska = engineer brigade (Wojska Inżynieryjne)
      brygada_saperska: { strength: 1550, provides: ['construction', 'route_clearance'] },
      // Brygada lotnictwa wojsk lądowych = army aviation brigade (W-3 Sokół, Mi-17, AW149)
      brygada_lotnictwa_wojsk_ladowych: { strength: 1800, provides: ['attack', 'lift', 'medevac'] }
    }
  },

  // NS-17: Armée de Terre — French doctrinal unit names throughout
  // Sources: Scorpion programme, LPM 2024-2030, defense.gouv.fr BIA structure,
  //   ALAT NH90 Caïman delivery (Feb 2025), B.LOG creation (March 2024).
  FRENCH: {
    battalion: {
      // Section transmissions = signal section (organic to régiment état-major)
      section_transmissions: { strength: 25, provides: ['communications'] },
      // Groupe médical de campagne = forward medical element (GTIA level, SSA personnel)
      groupe_medical_campagne: { strength: 28, provides: ['role_1'] },
      // Section ravitaillement = supply section (subsistances, carburant, munitions)
      section_ravitaillement: { strength: 15, provides: ['class_i', 'class_v'] }
    },
    brigade: {
      // Compagnie de Commandement et de Transmissions (CCT) — C2 and signal
      compagnie_commandement_transmissions: { strength: 100, provides: ['communications', 'cyber'] },
      // Compagnie médicale de campagne = forward medical company (SSA, Role 2 Basic)
      // Provides Groupement Médico-Chirurgical forward (GMC) — surgical, resus, triage
      compagnie_medicale: { strength: 130, provides: ['role_2_basic'] },
      // Bataillon de Soutien Logistique (BSL) — organic brigade-level CSS
      // Train regiment personnel; supply, petroleum, maintenance, transport
      bataillon_soutien_logistique: { strength: 440, provides: ['supply', 'maintenance'] },
      // Compagnie du génie = engineer company (Scorpion-era: mobility, breaching, C-IED)
      compagnie_genie: { strength: 130, provides: ['mobility', 'counter_mobility'] },
      // Groupe artillerie = artillery group (Caesar 6×6 wheeled SPH, CAESAR NG)
      groupe_artillerie: { strength: 480, provides: ['fire_support'] },
      // Escadron d'éclairage et d'investigation (EEI) = recce / ISR squadron
      escadron_eclairage_investigation: { strength: 110, provides: ['intelligence'] }
    },
    division: {
      // Bataillon de Commandement et de Transmissions (BCT) — division C2 and signal
      bataillon_commandement_transmissions: { strength: 350, provides: ['communications', 'cyber', 'ew'] },
      // Groupement Médico-Chirurgical (GMC) divisionnaire = Role 2 Enhanced
      // SSA (Service de Santé des Armées) provides; uses FLYP (lyophilized plasma) forward
      groupement_medico_chirurgical: { strength: 550, provides: ['role_2_enhanced'] },
      // Régiment de soutien logistique = divisional logistics regiment (Train, RMAT)
      regiment_soutien_logistique: { strength: 1700, provides: ['supply', 'maintenance', 'transport'] },
      // Bataillon du génie = engineer battalion (Génie de combat, route clearance, bridging)
      bataillon_genie: { strength: 520, provides: ['mobility', 'construction'] },
      // Régiment d'artillerie divisionnaire = divisional fires (Caesar NG, LRU MLRS)
      regiment_artillerie: { strength: 1600, provides: ['fire_support', 'mlrs'] },
      // Batterie de défense sol-air = air defense battery (Mistral, Crotale NG / SHORAD)
      batterie_defense_sol_air: { strength: 420, provides: ['shorad', 'c_uas'] }
    },
    corps: {
      // Brigade des Transmissions = corps signal brigade (strategic comms, cyber, EW)
      brigade_transmissions: { strength: 1000, provides: ['strategic_comms', 'cyber', 'space'] },
      // Régiment Médical (RMED) = corps medical regiment — Role 3
      // Based in Valbonne; SSA (Service de Santé des Armées) organic.
      // Uses French Lyophilized Plasma (FLYP/PLYO): room-temp storage, universal blood group,
      // <6 min reconstitution. 1:1 FLYP:RBC MTP ratio. Source: CTSA Clamart.
      regiment_medical: { strength: 900, provides: ['role_3'] },
      // Brigade Logistique (B.LOG) = corps theater logistics
      // Activated March 26, 2024. 8 Train regiments; 7000 active + 2600 reservists.
      // Designed to sustain 2 French brigades + 1 allied brigade at division level.
      brigade_logistique: { strength: 3500, provides: ['theater_logistics'] },
      // Brigade du Génie = engineer brigade (construction, route clearance, CBRN decontam)
      brigade_genie: { strength: 1600, provides: ['construction', 'route_clearance'] },
      // 4e Brigade d'Aérocombat (4e BAC) = ALAT army aviation brigade
      // NH90 Caïman TTH (63 delivered by Feb 2025, 10 stretchers MEDEVAC config),
      // Tigre HAD attack helicopter, Caracal SOF (4e RHFS).
      // Army-organic — not Air Force. MOU not required for MEDEVAC of French units.
      quatrieme_brigade_aerocombat: { strength: 2000, provides: ['attack', 'lift', 'medevac'] }
    }
  }
};

// ============================================================================
// TERRAIN DEFENSE MODIFIERS — Casualty rate adjustments by terrain/posture
// ============================================================================
// NOTE: icon values use direct emoji — unchanged from v2.6 (not in Phase 0 scope)

/**
 * Terrain defense modifiers affect casualty rates based on:
 * - Terrain type (provides cover/concealment)
 * - Operational posture (attack vs defense)
 * - Defender advantage in prepared positions
 *
 * Ukraine 2022-2024 Data:
 * - Urban defenders showed 40% lower casualties
 * - Fortified positions reduced casualties by 50%
 * - Open terrain attacks increased casualties by 30%
 */
export const TERRAIN_DEFENSE_MODIFIERS = {
  URBAN: {
    label: 'Urban Terrain',
    icon: '🏙️',
    ATTACK: {
      modifier: 1.35,
      description: 'Urban assault - significant attacker casualties',
      ukraine_data: 'Bakhmut: 3:1 attacker losses in urban fighting'
    },
    DEFENSE: {
      modifier: 0.60,
      description: 'Urban defense - defender advantage',
      ukraine_data: 'Prepared urban positions showed 40% casualty reduction'
    },
    DELAY: {
      modifier: 0.70,
      description: 'Urban delay operations'
    }
  },
  MOUNTAIN: {
    label: 'Mountainous Terrain',
    icon: '⛰️',
    ATTACK: {
      modifier: 1.25,
      description: 'Mountain assault - terrain disadvantage',
      note: 'Carpathian operations reference'
    },
    DEFENSE: {
      modifier: 0.65,
      description: 'Mountain defense - significant terrain advantage'
    },
    DELAY: {
      modifier: 0.60,
      description: 'Mountain delay - excellent defensive terrain'
    }
  },
  FOREST: {
    label: 'Forested Terrain',
    icon: '🌲',
    ATTACK: {
      modifier: 1.15,
      description: 'Forest assault - concealment cuts both ways'
    },
    DEFENSE: {
      modifier: 0.75,
      description: 'Forest defense - good concealment',
      ukraine_data: 'Kreminna forest fighting showed defensive advantage'
    },
    DELAY: {
      modifier: 0.70,
      description: 'Forest delay operations'
    }
  },
  OPEN: {
    label: 'Open Terrain',
    icon: '🏜️',
    ATTACK: {
      modifier: 1.20,
      description: 'Open terrain assault - high exposure',
      ukraine_data: 'Kherson offensive: drone surveillance negated concealment'
    },
    DEFENSE: {
      modifier: 0.85,
      description: 'Open terrain defense - limited cover'
    },
    DELAY: {
      modifier: 0.90,
      description: 'Open delay - difficult without prepared positions'
    }
  },
  MIXED: {
    label: 'Mixed Terrain',
    icon: '🗺️',
    ATTACK: {
      modifier: 1.10,
      description: 'Mixed terrain assault'
    },
    DEFENSE: {
      modifier: 0.80,
      description: 'Mixed terrain defense'
    },
    DELAY: {
      modifier: 0.75,
      description: 'Mixed terrain delay'
    }
  },
  FORTIFIED: {
    label: 'Fortified Positions',
    icon: '🏰',
    ATTACK: {
      modifier: 1.50,
      description: 'Assault on prepared positions',
      ukraine_data: 'Surovikin Line attacks: 2x-3x attacker casualties'
    },
    DEFENSE: {
      modifier: 0.50,
      description: 'Defense in prepared positions',
      ukraine_data: 'Fortified positions showed 50% casualty reduction'
    },
    DELAY: {
      modifier: 0.55,
      description: 'Delay from fortified positions'
    }
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get standard configuration for a national standard
 */
export function getStandardConfig(nationalStandard) {
  return NATIONAL_STANDARDS[nationalStandard] || NATIONAL_STANDARDS.NATO;
}

/**
 * Get default name for echelon
 */
export function getDefaultName(nationalStandard, echelon) {
  const standard = NATIONAL_STANDARDS[nationalStandard] || NATIONAL_STANDARDS.NATO;
  const echelonKey = echelon.toLowerCase();
  return standard[echelonKey]?.defaultName || echelon;
}

/**
 * Get default strength for echelon
 */
export function getDefaultStrength(nationalStandard, echelon) {
  const standard = NATIONAL_STANDARDS[nationalStandard] || NATIONAL_STANDARDS.NATO;
  const echelonKey = echelon.toLowerCase();
  return standard[echelonKey]?.strength || 0;
}

/**
 * Get subordinate count (brigades per division, etc.)
 */
export function getSubordinateCount(nationalStandard, echelon) {
  const standard = NATIONAL_STANDARDS[nationalStandard] || NATIONAL_STANDARDS.NATO;
  const echelonKey = echelon.toLowerCase();

  if (echelonKey === 'corps') return standard.corps?.divisions || 3;
  if (echelonKey === 'division') return standard.division?.brigades || 3;
  if (echelonKey === 'brigade') return standard.brigade?.battalions || 3;
  return 0;
}

/**
 * Get MTF position from FEBA
 */
export function getMTFPosition(nationalStandard, role) {
  const positions = MTF_POSITIONS[nationalStandard] || MTF_POSITIONS.NATO;
  const roleKey = role.toLowerCase().replace(' ', '_');
  return positions[roleKey] || 25;
}

/**
 * NS-07: Get HQ personnel count
 * Reads from NATIONAL_STANDARDS[nation][echelon].hq_personnel
 * Throws if data is missing — no silent fallback (APP_RULES 6.1)
 *
 * @param {string} nationalStandard - Nation key (ROMANIAN, NATO, US, etc.)
 * @param {string} echelon - Echelon key (battalion, brigade, division, corps)
 * @returns {number} HQ personnel count
 * @throws {Error} If nation or echelon not found in NATIONAL_STANDARDS
 */
export function getHQPersonnel(nationalStandard, echelon) {
  const echelonKey = echelon.toLowerCase();
  const value = NATIONAL_STANDARDS[nationalStandard]?.[echelonKey]?.hq_personnel;
  if (value === undefined) {
    throw new Error(
      `getHQPersonnel: missing hq_personnel for nation="${nationalStandard}" echelon="${echelonKey}". ` +
      `Valid nations: ${Object.keys(NATIONAL_STANDARDS).join(', ')}`
    );
  }
  return value;
}

/**
 * NS-08: Get HQ casualty modifier
 * Reads from NATIONAL_STANDARDS[nation][echelon].hq_casualty_modifier
 * Throws if data is missing (APP_RULES 6.1)
 *
 * @param {string} nationalStandard - Nation key
 * @param {string} echelon - Echelon key
 * @returns {number} HQ casualty modifier (fraction, e.g. 0.20)
 * @throws {Error} If nation or echelon not found
 */
export function getHQCasualtyModifier(nationalStandard, echelon) {
  const echelonKey = echelon.toLowerCase();
  const value = NATIONAL_STANDARDS[nationalStandard]?.[echelonKey]?.hq_casualty_modifier;
  if (value === undefined) {
    throw new Error(
      `getHQCasualtyModifier: missing hq_casualty_modifier for nation="${nationalStandard}" echelon="${echelonKey}". ` +
      `Valid nations: ${Object.keys(NATIONAL_STANDARDS).join(', ')}`
    );
  }
  return value;
}

/**
 * NS-09: Get HQ position from FEBA (km)
 * Reads from NATIONAL_STANDARDS[nation][echelon].hq_position_feba_km
 * Throws if data is missing (APP_RULES 6.1)
 *
 * @param {string} nationalStandard - Nation key
 * @param {string} echelon - Echelon key
 * @returns {number} HQ distance from FEBA in km
 * @throws {Error} If nation or echelon not found
 */
export function getHQPosition(nationalStandard, echelon) {
  const echelonKey = echelon.toLowerCase();
  const value = NATIONAL_STANDARDS[nationalStandard]?.[echelonKey]?.hq_position_feba_km;
  if (value === undefined) {
    throw new Error(
      `getHQPosition: missing hq_position_feba_km for nation="${nationalStandard}" echelon="${echelonKey}". ` +
      `Valid nations: ${Object.keys(NATIONAL_STANDARDS).join(', ')}`
    );
  }
  return value;
}

/**
 * NS-10: Get echelon breakdown (computed — no longer reads ECHELON_BREAKDOWN standalone)
 * Computes all breakdown values directly from NATIONAL_STANDARDS[nation][echelon]
 * Throws if data is missing (APP_RULES 6.1)
 *
 * @param {string} nationalStandard - Nation key
 * @param {string} echelon - Echelon key (battalion, brigade, division, corps)
 * @returns {Object} Breakdown with total_strength, hq_strength, combat_strength,
 *   support_strength, hq_percent, combat_percent
 * @throws {Error} If nation or echelon not found
 */
export function getEchelonBreakdown(nationalStandard, echelon) {
  const echelonKey = echelon.toLowerCase();
  const echelonData = NATIONAL_STANDARDS[nationalStandard]?.[echelonKey];
  if (!echelonData) {
    throw new Error(
      `getEchelonBreakdown: no data for nation="${nationalStandard}" echelon="${echelonKey}". ` +
      `Valid nations: ${Object.keys(NATIONAL_STANDARDS).join(', ')}`
    );
  }
  const hq_strength = echelonData.hq_personnel;
  const combat_strength = Math.round(echelonData.strength * echelonData.combat_ratio);
  const support_strength = echelonData.strength - hq_strength - combat_strength;
  return {
    total_strength: echelonData.strength,
    hq_strength,
    combat_strength,
    support_strength,
    hq_percent: hq_strength / echelonData.strength,
    combat_percent: echelonData.combat_ratio
  };
}

/**
 * Get support units for a specific echelon
 */
export function getSupportUnitsForEchelon(nationalStandard, echelon) {
  // NS-16: Zero-fallback policy — no silent NATO default. Unknown standard = explicit error.
  // All 8 selectable standards (ROMANIAN/NATO/US/MULTINATIONAL/GERMAN/BRITISH/POLISH/FRENCH)
  // are defined in SUPPORT_UNITS_BY_ECHELON. If this throws, a new national standard was
  // added to NATIONAL_STANDARDS without a corresponding SUPPORT_UNITS_BY_ECHELON entry.
  if (!nationalStandard) {
    throw new Error('getSupportUnitsForEchelon: nationalStandard is required');
  }
  const standard = nationalStandard.toUpperCase();
  if (!SUPPORT_UNITS_BY_ECHELON[standard]) {
    throw new Error('getSupportUnitsForEchelon: No support units defined for standard "' + standard + '". Add entry to SUPPORT_UNITS_BY_ECHELON.');
  }
  const echelonKey = echelon?.toLowerCase() || 'brigade';

  const supportByEchelon = SUPPORT_UNITS_BY_ECHELON[standard];
  const echelonSupport = supportByEchelon[echelonKey] || {};

  let totalStrength = 0;
  const units = [];

  Object.entries(echelonSupport).forEach(([unitName, unitData]) => {
    totalStrength += unitData.strength || 0;
    units.push({
      name: unitName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      code: unitName,
      strength: unitData.strength,
      provides: unitData.provides || []
    });
  });

  let attachedSupport = [];
  if (echelonKey === 'brigade' && supportByEchelon.division) {
    attachedSupport.push({
      source_echelon: 'division',
      note: 'Division assets may be attached for specific operations'
    });
  }
  if (echelonKey === 'division' && supportByEchelon.corps) {
    attachedSupport.push({
      source_echelon: 'corps',
      note: 'Corps assets may be attached for major operations'
    });
  }

  return {
    national_standard: standard,
    echelon: echelonKey,
    organic_units: units,
    total_organic_strength: totalStrength,
    attached_support: attachedSupport,
    by_category: {
      medical: units.filter(u => u.provides.some(p => p.includes('role_'))),
      logistics: units.filter(u => u.provides.some(p => ['supply', 'maintenance', 'transport'].includes(p))),
      signal: units.filter(u => u.provides.some(p => p.includes('communication'))),
      engineer: units.filter(u => u.provides.some(p => ['mobility', 'construction'].includes(p))),
      fires: units.filter(u => u.provides.some(p => p.includes('fire_support')))
    },
    icon: MILITARY_ICONS?.SUPPORT || '🔧'
  };
}

/**
 * Get terrain defense modifier for casualty calculations
 */
export function getTerrainDefenseModifier(terrain, posture) {
  const terrainKey = terrain?.toUpperCase() || 'MIXED';
  const postureKey = posture?.toUpperCase() || 'ATTACK';

  const terrainData = TERRAIN_DEFENSE_MODIFIERS[terrainKey] || TERRAIN_DEFENSE_MODIFIERS.MIXED;
  const postureData = terrainData[postureKey] || terrainData.ATTACK;

  const normalizedPosture = postureKey.includes('ATTACK') ? 'ATTACK'
    : postureKey.includes('DEFENSE') || postureKey.includes('DEFEND') ? 'DEFENSE'
    : postureKey.includes('DELAY') || postureKey.includes('WITHDRAWAL') ? 'DELAY'
    : 'ATTACK';

  const finalData = terrainData[normalizedPosture] || postureData;

  return {
    terrain,
    terrain_label: terrainData.label,
    terrain_icon: terrainData.icon,
    posture: normalizedPosture,
    modifier: finalData.modifier,
    description: finalData.description,
    ukraine_data: finalData.ukraine_data || null,
    casualty_effect: finalData.modifier > 1.0
      ? `+${Math.round((finalData.modifier - 1) * 100)}% casualties`
      : `-${Math.round((1 - finalData.modifier) * 100)}% casualties`,
    is_attacker_disadvantage: normalizedPosture === 'ATTACK' && finalData.modifier > 1.0,
    is_defender_advantage: normalizedPosture === 'DEFENSE' && finalData.modifier < 1.0,
    icon: finalData.modifier <= 0.70 ? (STATUS_ICONS?.GREEN || '🟢')
      : finalData.modifier <= 0.90 ? (STATUS_ICONS?.YELLOW || '🟡')
      : finalData.modifier <= 1.10 ? (STATUS_ICONS?.YELLOW || '🟡')
      : (STATUS_ICONS?.RED || '🔴')
  };
}

/**
 * Get terrain options for UI dropdown
 */
export function getTerrainOptions() {
  return Object.entries(TERRAIN_DEFENSE_MODIFIERS).map(([key, data]) => ({
    value: key,
    label: data.label,
    icon: data.icon
  }));
}

/**
 * NS-11: Calculate total HQ personnel for a force structure
 * Uses getHQPersonnel() — no longer reads deleted HQ_PERSONNEL standalone constant
 *
 * @param {Object} forceStructure - { corps, divisions, brigades, battalions }
 * @param {string} nationalStandard - Nation key
 * @returns {Object} { total, breakdown, standard_used, icon }
 */
export function calculateTotalHQPersonnel(forceStructure, nationalStandard) {
  const breakdown = {
    corps: (forceStructure.corps || 0) * getHQPersonnel(nationalStandard, 'corps'),
    division: (forceStructure.divisions || 0) * getHQPersonnel(nationalStandard, 'division'),
    brigade: (forceStructure.brigades || 0) * getHQPersonnel(nationalStandard, 'brigade'),
    battalion: (forceStructure.battalions || 0) * getHQPersonnel(nationalStandard, 'battalion')
  };

  return {
    total: breakdown.corps + breakdown.division + breakdown.brigade + breakdown.battalion,
    breakdown,
    standard_used: nationalStandard,
    icon: MILITARY_ICONS.HQ
  };
}

/**
 * NS-12: Calculate expected HQ casualties
 * Uses getHQPersonnel() and getHQCasualtyModifier() — no longer reads deleted standalone constants
 *
 * @param {Object} forceStructure - { corps, divisions, brigades, battalions }
 * @param {string} nationalStandard - Nation key
 * @param {number} baseCasualtyRate - Base daily casualty rate (fraction)
 * @param {number} days - Operation duration in days
 * @returns {Object} { casualties, total, parameters, icon }
 */
export function calculateHQCasualties(forceStructure, nationalStandard, baseCasualtyRate, days) {
  const casualties = {
    corps: 0,
    division: 0,
    brigade: 0,
    battalion: 0
  };

  if (forceStructure.corps) {
    casualties.corps = Math.round(
      forceStructure.corps
      * getHQPersonnel(nationalStandard, 'corps')
      * baseCasualtyRate
      * getHQCasualtyModifier(nationalStandard, 'corps')
      * days
    );
  }
  if (forceStructure.divisions) {
    casualties.division = Math.round(
      forceStructure.divisions
      * getHQPersonnel(nationalStandard, 'division')
      * baseCasualtyRate
      * getHQCasualtyModifier(nationalStandard, 'division')
      * days
    );
  }
  if (forceStructure.brigades) {
    casualties.brigade = Math.round(
      forceStructure.brigades
      * getHQPersonnel(nationalStandard, 'brigade')
      * baseCasualtyRate
      * getHQCasualtyModifier(nationalStandard, 'brigade')
      * days
    );
  }
  if (forceStructure.battalions) {
    casualties.battalion = Math.round(
      forceStructure.battalions
      * getHQPersonnel(nationalStandard, 'battalion')
      * baseCasualtyRate
      * getHQCasualtyModifier(nationalStandard, 'battalion')
      * days
    );
  }

  return {
    casualties,
    total: casualties.corps + casualties.division + casualties.brigade + casualties.battalion,
    parameters: {
      base_rate: baseCasualtyRate,
      days,
      national_standard: nationalStandard
    },
    icon: STATUS_ICONS.YELLOW
  };
}

/**
 * Get national standard options for dropdown
 */
export function getNationalStandardOptions() {
  return Object.values(NATIONAL_STANDARDS).map(std => ({
    value: std.id,
    label: std.label,
    shortLabel: std.shortLabel,
    icon: std.icon
  }));
}

// ============================================================================
// v2.1 CSS-004: AVAILABLE BRIGADE TYPES HELPERS
// ============================================================================

/**
 * Get available brigade types for a national standard
 * Used by Tab1 to filter brigade type dropdown
 *
 * @param {string} nationalStandard - National standard ID
 * @returns {string[]} Array of available brigade type keys
 */
export function getAvailableBrigadeTypes(nationalStandard) {
  const standard = NATIONAL_STANDARDS[nationalStandard];
  if (!standard || !standard.available_brigade_types) {
    return ['HEAVY', 'INFANTRY', 'MECHANIZED', 'ARTILLERY', 'LOGISTICS', 'MEDICAL'];
  }
  return standard.available_brigade_types;
}

/**
 * Get default CSS brigade type for a national standard
 *
 * @param {string} nationalStandard - National standard ID
 * @returns {string} Default CSS brigade type
 */
export function getDefaultCSSType(nationalStandard) {
  const standard = NATIONAL_STANDARDS[nationalStandard];
  return standard?.css_default || 'LOGISTICS';
}

/**
 * Check if a brigade type is available for a national standard
 *
 * @param {string} nationalStandard - National standard ID
 * @param {string} brigadeType - Brigade type to check
 * @returns {boolean} True if brigade type is available
 */
export function isBrigadeTypeAvailable(nationalStandard, brigadeType) {
  const availableTypes = getAvailableBrigadeTypes(nationalStandard);
  return availableTypes.includes(brigadeType);
}

/**
 * Get union of available brigade types for multiple national standards
 * Used for multinational corps with divisions of different nations
 *
 * @param {string[]} nationalStandards - Array of national standard IDs
 * @returns {string[]} Union of all available brigade types
 */
export function getUnionBrigadeTypes(nationalStandards) {
  const allTypes = new Set();
  nationalStandards.forEach(std => {
    const types = getAvailableBrigadeTypes(std);
    types.forEach(type => allTypes.add(type));
  });
  return Array.from(allTypes);
}

// ============================================================================
// EDUCATIONAL CONTENT
// ============================================================================

export const NATIONAL_STANDARDS_EDUCATIONAL = {
  key_principles: [
    'Romanian brigades have 4 battalions vs NATO 3',
    'Polish brigades also have 4 maneuver battalions — matching Romanian doctrine',
    'US BCT is lean (4500) vs NATO heavy brigade (5000)',
    'German Bundeswehr battalion is larger (1000) reflecting full Stabskompanie',
    'British battalion is smaller (750) — battlegroup-centric doctrine',
    'HQ personnel at corps level: 1500–2500 depending on nation (ROU lean, US large)',
    'HQ personnel at division level: 750–1200 depending on nation',
    'Combat ratio decreases at higher echelons (more support)',
    'HQ positioning from FEBA varies by doctrine: ROU/POL (forward/aggressive), NATO/GBR (balanced), US (conservative/protected)',
    'Modern artillery ranges (30–40 km+) require graduated HQ standoff distances',
    'Battalion HQs closer to FEBA (7–12 km), Corps HQs far behind (50–60 km) for survivability',
    'Rear area security varies: US RASO doctrine most comprehensive (0.80 modifier), Polish WOT integration significant (0.88)'
  ],

  hq_positioning_doctrine: [
    'ROMANIAN: Forward positioning (Bn 8 km, Bde 15 km, Div 25 km, Corps 50 km) — aggressive doctrine',
    'NATO: Balanced positioning (Bn 10 km, Bde 18 km, Div 28 km, Corps 55 km) — standard doctrine',
    'US: Conservative positioning (Bn 12 km, Bde 20 km, Div 30 km, Corps 60 km) — prioritizes HQ survivability',
    'GERMAN: Bundeswehr balanced (Bn 10 km, Bde 18 km, Div 30 km, Korps 60 km) — similar to US at higher echelons',
    'BRITISH: NATO-equivalent positioning (Bn 10 km, Bde 18 km, Div 28 km, Corps 55 km)',
    'POLISH: Forward positioning (Bn 8 km, Bde 15 km, Div 28 km, Korpus 55 km) — front-line nation posture',
    'MTF positions adjust automatically via offset system when HQ positions change'
  ],

  ukraine_lessons: [
    'Ukrainian brigades adapted to be more independent',
    'Smaller, dispersed HQs survived better against precision strikes',
    'Support units required forward positioning',
    'Medical units integrated at lower echelons',
    'Russian artillery (30–40 km range) made static HQs vulnerable',
    'Frequent HQ displacement became essential for survival',
    'WOT-equivalent territorial forces proved critical for rear area security'
  ],

  support_unit_doctrine: {
    GERMAN: 'Bundeswehr organizes CSS through Versorgungsbataillon at brigade, Logistikbataillon at division, Versorgungsregiment at corps. Medical service (Sanitätsdienst) is a separate armed forces branch.',
    BRITISH: 'RLC provides Close Support Logistic Regiment at brigade, Force Support Logistic Regiment at division, 101 Operational Sustainment Brigade at theatre. RAMC provides Field Ambulance (brigade), Medical Regiment (division), Close Support Medical Group (corps).',
    POLISH: 'Wojska Lądowe CSS organized through Batalion Logistyczny at brigade, Pułk Logistyczny at division, Brygada Logistyczna at corps. Wojskowa Służba Zdrowia (Military Medical Service) provides Kompania Medyczna → Batalion Medyczny → Brygada Medyczna.'
  }
};

// ============================================================================
// NS-16: DEFAULT EXPORT — deleted constants removed
// HQ_PERSONNEL, HQ_CASUALTY_MODIFIERS, HQ_POSITIONS, ECHELON_BREAKDOWN
// are not exported (constants deleted per NS-03 through NS-06)
// ============================================================================

const nationalStandardsConstants = {
  NATIONAL_STANDARDS,
  MTF_POSITIONS,
  SUPPORT_UNITS,
  SUPPORT_UNITS_BY_ECHELON,
  TERRAIN_DEFENSE_MODIFIERS,
  getStandardConfig,
  getDefaultName,
  getDefaultStrength,
  getSubordinateCount,
  getMTFPosition,
  getHQPersonnel,
  getHQCasualtyModifier,
  getHQPosition,
  getEchelonBreakdown,
  getSupportUnitsForEchelon,
  getTerrainDefenseModifier,
  getTerrainOptions,
  calculateTotalHQPersonnel,
  calculateHQCasualties,
  getNationalStandardOptions,
  getAvailableBrigadeTypes,
  getDefaultCSSType,
  isBrigadeTypeAvailable,
  getUnionBrigadeTypes,
  NATIONAL_STANDARDS_EDUCATIONAL
};

export default nationalStandardsConstants;
